import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AlertCircle, Flag, Info } from 'lucide-react';
import DashboardCard from './DashboardCard';
import Button from '../../../components/ui/Button';
import { SEVERITY_COLORS, formatTry } from '../constants';

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #F0F2F5;

  &:last-child { border-bottom: none; }
`;

const IconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color }) => `${$color}1A`};
  color: ${({ $color }) => $color};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Desc = styled.div`
  font-size: 12px;
  color: #6B7280;
  margin-top: 2px;
`;

const ActionBtn = styled(Button)`
  padding: 5px 12px;
  font-size: 12px;
  border-width: 1.5px;
  flex-shrink: 0;
`;

const ICONS = { critical: AlertCircle, warning: Flag, info: Info };

export default function RiskSignalsCard({ slice }) {
  const { t } = useTranslation();
  const history = useHistory();
  const signals = slice.data?.signals || [];

  const buildTexts = useCallback((signal) => {
    switch (signal.type) {
      case 'limitViolation':
        return {
          title: t('expenseDashboard.risks.limitViolation.title', { name: signal.userName || '—' }),
          desc: t('expenseDashboard.risks.limitViolation.description', {
            category: signal.categoryName || '—',
            amount: formatTry(signal.amount),
            limit: signal.limitAmount != null ? formatTry(signal.limitAmount) : '—',
            count: signal.count,
          }),
          action: t('expenseDashboard.risks.limitViolation.action'),
        };
      case 'sameDayRepeat':
        return {
          title: t('expenseDashboard.risks.sameDayRepeat.title', { name: signal.userName || '—', count: signal.count }),
          desc: t('expenseDashboard.risks.sameDayRepeat.description'),
          action: t('expenseDashboard.risks.sameDayRepeat.action'),
        };
      case 'weekendEntry':
        return {
          title: t('expenseDashboard.risks.weekendEntry.title', { count: signal.count }),
          desc: t('expenseDashboard.risks.weekendEntry.description'),
          action: t('expenseDashboard.risks.weekendEntry.action'),
        };
      case 'missingReceipt':
        return {
          title: t('expenseDashboard.risks.missingReceipt.title', { count: signal.count }),
          desc: t('expenseDashboard.risks.missingReceipt.description'),
          action: t('expenseDashboard.risks.missingReceipt.action'),
        };
      case 'budgetWarn':
        return {
          title: t('expenseDashboard.risks.budgetWarn.title', {
            target: signal.departmentName || signal.locationName || t('expenseDashboard.risks.budgetWarn.company'),
            pct: signal.percent,
          }),
          desc: t('expenseDashboard.risks.budgetWarn.description', {
            spent: formatTry(signal.amount),
            budget: signal.limitAmount != null ? formatTry(signal.limitAmount) : '—',
          }),
          action: t('expenseDashboard.risks.budgetWarn.action'),
        };
      case 'locationSurge':
      default:
        return {
          title: t('expenseDashboard.risks.locationSurge.title', { location: signal.locationName || '—', pct: signal.percent }),
          desc: t('expenseDashboard.risks.locationSurge.description', {
            current: formatTry(signal.amount),
            prev: signal.limitAmount != null ? formatTry(signal.limitAmount) : '—',
          }),
          action: t('expenseDashboard.risks.locationSurge.action'),
        };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleAction = useCallback((signal) => {
    switch (signal.type) {
      case 'limitViolation':
        if (signal.expenseId) history.push(`/expenses/${signal.expenseId}`);
        else history.push('/all-expenses');
        break;
      case 'budgetWarn':
        history.push('/expense-budgets');
        break;
      case 'sameDayRepeat':
      case 'weekendEntry':
      case 'missingReceipt':
      case 'locationSurge':
      default:
        history.push('/all-expenses');
        break;
    }
  }, [history]);

  return (
    <DashboardCard
      cardKey="anomali-risk-sinyalleri"
      title={t('expenseDashboard.risks.title')}
      meta={t('expenseDashboard.risks.meta')}
      span={4}
      spanMd={6}
      loading={slice.loading}
      empty={signals.length === 0}
      emptyText={t('expenseDashboard.risks.empty')}
    >
      <div>
        {signals.map((signal, index) => {
          const color = SEVERITY_COLORS[signal.severity] || SEVERITY_COLORS.info;
          const IconComp = ICONS[signal.severity] || Info;
          const texts = buildTexts(signal);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Row key={`${signal.type}-${index}`}>
              <IconWrap $color={color}><IconComp size={15} /></IconWrap>
              <Body>
                <Title>{texts.title}</Title>
                <Desc>{texts.desc}</Desc>
              </Body>
              <ActionBtn variant="outline" onClick={() => handleAction(signal)}>
                {texts.action}
              </ActionBtn>
            </Row>
          );
        })}
      </div>
    </DashboardCard>
  );
}
